import {Placement} from '@popperjs/core';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {AsyncHandlerOf, Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Dropdown} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {renderInlineList} from '../../../../helpers/listHelpers';
import {AccountModel} from '../../../../models/api/accountModel';
import {DomainModel, DomainValidityStatusesEnum} from '../../../../models/api/domainModel';
import {EmailDomainsInputContainer} from '../../../common/createFormInputs/emailDomainsInputContainer';
import {IconBox} from '../../../common/iconBox/iconBox';
import {
  AccountDetailsEditActionsEnum,
  AccountDetailsEditAttributesEnum,
  buildAccountDetailsEditPayload,
} from '../../tracking/accountDetailsTrackingHelpers';
import {useAccountDetailsContext} from '../accountDetailsContext';

/*
 * Constants.
 */

const dropdownWidth = 300; // px

/*
 * Types.
 */

export enum EmailDomainDropdownModesEnum {
  ADD = 'ADD',
  EDIT = 'EDIT',
}

interface EmailDomainDropdownProps {
  account: AccountModel;
  mode: EmailDomainDropdownModesEnum;
  popoverTransferProps: PopoverTransferProps;
  placement?: Placement;
  onCancel: Handler;
  onSave: Handler;
  updateDomains: AsyncHandlerOf<ReadonlyArray<string>>;
}

/*
 * Style.
 */

const StyledDropdownContentDiv = styled.div`
  padding: 0 20px 20px 20px;
`;

const StyledButtonsDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 5px;
  margin-top: 20px;
`;

const StyledIconBox = styled(IconBox)`
  margin-top: 13px;
`;

const StyledDomainsRemovedWarningTextDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledDomainNamesRemovedSpan = styled.span`
  font-weight: ${fontWeights.semibold};
`;

/*
 * Component.
 */

export const EmailDomainDropdown: FC<EmailDomainDropdownProps> = (props) => {
  const {account, mode, popoverTransferProps, placement, onCancel, onSave, updateDomains} = props;
  const [domains, setDomains] = useState<ReadonlyArray<DomainModel>>(
    buildDomainsFromAccountDomainStrings(account),
  );
  const [isSaving, setIsSaving] = useState<boolean>();
  const [shouldDisable, setShouldDisable] = useState<boolean>(false);
  const {onTrackEvent} = useAccountDetailsContext();

  const onSaveClick = async () => {
    const domainNames = domains.map((domain) => domain.name);
    setIsSaving(true);
    await updateDomains(domainNames);

    onTrackEvent(
      buildAccountDetailsEditPayload(
        account.id,
        computeTrackingEventEnum(account, domains),
        AccountDetailsEditAttributesEnum.DOMAINS,
      ),
    );

    onSave();
    setIsSaving(false);
  };

  return (
    <Dropdown {...popoverTransferProps} width={dropdownWidth} hasBackground placement={placement}>
      <DropdownHeader title={renderTitle(mode)} />
      <StyledDropdownContentDiv>
        <EmailDomainsInputContainer
          domains={domains}
          updateDomains={setDomains}
          onTextChange={(text) => setShouldDisable(text !== '')}
        />
        {renderDomainsRemovedWarning(account, domains)}
        <StyledButtonsDiv>
          <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onCancel}>
            <Trans id="account-information-email-domain-dropdown-cancel">Cancel</Trans>
          </Button>
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            onClick={onSaveClick}
            isDisabled={isSaving || !computeCanSave(account, domains) || shouldDisable}
          >
            {renderPrimaryButtonText(mode)}
          </Button>
        </StyledButtonsDiv>
      </StyledDropdownContentDiv>
    </Dropdown>
  );
};

/*
 * Helpers.
 */

const computeTrackingEventEnum = (
  account: AccountModel,
  domains: ReadonlyArray<DomainModel>,
): AccountDetailsEditActionsEnum => {
  if (account.domains.length === 0 && domains.length > 0) {
    return AccountDetailsEditActionsEnum.ADD;
  }
  return AccountDetailsEditActionsEnum.EDIT;
};

function buildDomainsFromAccountDomainStrings(account: AccountModel): ReadonlyArray<DomainModel> {
  return account.domains.map((domain) => ({
    name: domain,
    validityStatus: DomainValidityStatusesEnum.VALID,
  }));
}

function renderTitle(mode: EmailDomainDropdownModesEnum) {
  switch (mode) {
    case EmailDomainDropdownModesEnum.ADD:
      return <Trans id="account-information-add-email-domains">Add email domains</Trans>;
    case EmailDomainDropdownModesEnum.EDIT:
      return <Trans id="account-information-edit-email-domains">Edit email domains</Trans>;
    default:
      return null;
  }
}

function renderPrimaryButtonText(mode: EmailDomainDropdownModesEnum) {
  switch (mode) {
    case EmailDomainDropdownModesEnum.ADD:
      return <Trans id="account-information-email-domain-dropdown-add">Add</Trans>;
    case EmailDomainDropdownModesEnum.EDIT:
      return <Trans id="account-information-email-domain-dropdown-save">Save</Trans>;
    default:
      return null;
  }
}

function computeCanSave(account: AccountModel, domains: ReadonlyArray<DomainModel>) {
  if (domains.length === 0 && account.domains.length === 0) {
    return false;
  }

  if (domains.some((domain) => domain.validityStatus !== DomainValidityStatusesEnum.VALID)) {
    return false;
  }

  return true;
}

function renderDomainsRemovedWarning(account: AccountModel, domains: ReadonlyArray<DomainModel>) {
  const domainNames = domains.map((d) => d.name);
  const domainsRemoved = account.domains.filter((domain) => !domainNames.includes(domain));
  if (domainsRemoved.length === 0) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledIconBox iconName="infoFilled" iconSize={13} iconColor={theme.palette.blue.shade40}>
          <StyledDomainsRemovedWarningTextDiv>
            <Plural
              id="account-information-remove-domain-warning"
              value={domainsRemoved.length}
              one={
                <>
                  Contacts with the{' '}
                  <StyledDomainNamesRemovedSpan>{domainsRemoved[0]}</StyledDomainNamesRemovedSpan> email
                  domain will be disassociated from this Account.
                </>
              }
              other={
                <>
                  Contacts with the{' '}
                  <StyledDomainNamesRemovedSpan>
                    {renderInlineList(domainsRemoved)}
                  </StyledDomainNamesRemovedSpan>{' '}
                  email domains will be disassociated from this Account.
                </>
              }
            />
          </StyledDomainsRemovedWarningTextDiv>
        </StyledIconBox>
      )}
    </UserThemeConsumer>
  );
}
